import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Check, GraduationCap, Trophy, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TIER_ICONS = {
  "neet-pg-inicet": Crown,
  "neet-pg": Trophy,
  "ini-cet": GraduationCap,
};

const PLAN_ORDER = ["monthly", "quarterly", "half-yearly", "annual"];

function TierCard({ tier, defaultPlan = "annual", onSubscribe, loading, activeTierId }) {
  const Icon = TIER_ICONS[tier.id] || Crown;
  const [selected, setSelected] = useState(defaultPlan);
  const isLoadingThis = loading && activeTierId === tier.id;
  const plans = PLAN_ORDER.map((k) => tier.plans[k]).filter(Boolean);

  return (
    <div
      data-testid={`pricing-tier-${tier.id}`}
      className={`relative bg-white rounded-3xl border-2 p-5 sm:p-6 flex flex-col ${
        tier.recommended ? "border-blue-700 pricing-glow" : "border-slate-200"
      }`}
    >
      {tier.recommended && (
        <div className="absolute -top-3 right-6 bg-blue-700 text-white text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-full">
          Recommended
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-2xl bg-blue-50 ring-1 ring-blue-600/15 flex items-center justify-center shrink-0">
          <Icon className="h-6 w-6 text-blue-700" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg sm:text-xl font-extrabold text-slate-900 leading-tight">{tier.name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{tier.tagline}</p>
        </div>
        <span className="inline-flex items-center gap-1 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full shrink-0">
          <CheckCircle2 className="h-3 w-3" /> Active
        </span>
      </div>

      {/* Features */}
      <ul className="mt-5 space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
            <Check className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" strokeWidth={3} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* Plan options */}
      <div className="mt-5 space-y-2.5">
        {plans.map((p) => {
          const isActive = selected === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelected(p.id)}
              data-testid={`pricing-plan-${tier.id}-${p.id}`}
              className={`w-full text-left rounded-2xl border-2 transition-all px-4 py-3 flex items-center gap-3 ${
                isActive ? "border-blue-600 bg-blue-50/60" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <span className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                isActive ? "border-blue-600" : "border-slate-300"
              }`}>
                {isActive && <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900">{p.name}</span>
                  {p.save && (
                    <span className="bg-blue-600 text-white text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded">
                      Save {p.save}%
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">≈ ₹{p.per_month}/month</div>
              </div>
              <div className="text-right shrink-0">
                {p.original && (
                  <div className="text-xs text-slate-400 line-through leading-none">₹{p.original}</div>
                )}
                <div className="font-heading text-lg font-extrabold text-slate-900 leading-tight">₹{p.price}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Subscribe */}
      <Button
        onClick={() => onSubscribe(tier.id, selected)}
        disabled={loading}
        data-testid={`pricing-subscribe-${tier.id}`}
        className="mt-5 h-12 w-full bg-blue-700 hover:bg-blue-800 text-white rounded-2xl font-bold text-base shadow-md shadow-blue-700/20"
      >
        {isLoadingThis ? "Processing…" : "Subscribe"}
      </Button>
    </div>
  );
}

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTierId, setActiveTierId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/plans")
      .then(({ data }) => setTiers(data.tiers || []))
      .catch(() => setTiers([]));
  }, []);

  const handleSubscribe = async (tierId, planKey) => {
    if (!user) {
      toast.info("Please log in or sign up to continue with your subscription.");
      navigate("/register");
      return;
    }
    setLoading(true);
    setActiveTierId(tierId);
    try {
      const { data } = await api.post("/checkout", { plan_id: `${tierId}:${planKey}` });
      toast.success(data.message || "Plan selected. Payment gateway coming soon.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
      setActiveTierId(null);
    }
  };

  return (
    <section id="pricing" className="py-20 md:py-28 bg-slate-50/60 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
            <Crown className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">DearDr Pro</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mt-4">
            NEET PG &amp; INI-CET, covered
          </h2>
          <p className="text-slate-600 mt-3">
            Pick the plan that fits your prep. ₹299/mo per exam &middot; ₹499/mo for both &middot; cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 items-start">
          {tiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              defaultPlan="annual"
              onSubscribe={handleSubscribe}
              loading={loading}
              activeTierId={activeTierId}
            />
          ))}
        </div>

        <p className="text-center text-xs text-slate-500 mt-8">
          Secure payment via <b className="text-slate-700">Razorpay</b> &middot; All amounts in INR &middot; Payment gateway integration coming soon
        </p>
      </div>
    </section>
  );
}
